import {
  useQuery,
  UseQueryResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { getAllUsers, createUser } from '@/apis/ApiUtil';
import { assignUserRole } from '@/apis/assignUserRole';
import { fetchTeams } from '@/apis/fetchTeams';
import { fetchTeamMembers } from '@/apis/fetchTeamMembers';
import { createTeam, CreateTeamRequest } from '@/apis/createTeam';
import { deleteTeam } from '@/apis/deleteTeam';
import { addUserToTeam, removeUserFromTeam } from '@/apis/addAndRemoveFromTeam';
import { removeMemberFromOrganizationRequest } from '@/apis/removeMemberFromOrganization';
import headerObject from '@/types/headerObj';
import User from '@/types/userType';
import { useToast } from '@/hooks/useToast';

interface TeamData {
  users: User[];
  teams: any[];
}

// Hook for fetching all users
export const useAllUsers = (
  organizationUUID: string | undefined,
  headerObj: headerObject
): UseQueryResult<User[]> => {
  return useQuery<User[]>({
    queryKey: ['users', 'all', organizationUUID],
    queryFn: async () => {
      if (!organizationUUID) return [];
      const data = await getAllUsers(organizationUUID, headerObj);
      return data?.Users ?? [];
    },
    enabled: !!organizationUUID,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook for fetching teams with members
export const useTeams = (
  organizationUUID: string | undefined
): UseQueryResult<any[]> => {
  return useQuery<any[]>({
    queryKey: ['teams', 'all', organizationUUID],
    queryFn: async () => {
      if (!organizationUUID) return [];
      const data = await fetchTeams({ OrgUUID: organizationUUID });
      const teams = Array.isArray(data)
        ? data
        : data?.Teams || data?.teams || [];

      // Fetch team members for each team
      const teamsWithMembers = await Promise.all(
        teams.map(async (team: any) => {
          const members = await fetchTeamMembers({
            TeamUUID: team.TeamUUID,
            OrgUUID: organizationUUID,
          });
          return {
            TeamName: team?.TeamName,
            TeamMembers:
              members.Members?.map((member: User) => ({
                avatar_url: member.Image,
                login: member.GithubUsername || member.GitlabUsername,
                UserUUID: member.UserUUID,
                Email: member.Email,
                GithubUsername: member.GithubUsername,
                GitlabUsername: member.GitlabUsername,
                BitbucketUsername: member.BitbucketUsername,
                // Display name, so callers resolving a roster member from a
                // full-name key (output_per_engineer_chart uses one for mapped
                // users) have something to match on. `login` only carries the
                // first git handle a member has.
                Name: [member.FirstName, member.LastName]
                  .filter(Boolean)
                  .join(' ')
                  .trim(),
              })) || [],
            TeamUUID: team.TeamUUID,
          };
        })
      );

      return teamsWithMembers;
    },
    enabled: !!organizationUUID,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Combined hook for both users and teams
export const useTeamData = (
  organizationUUID: string | undefined,
  headerObj: headerObject
): {
  users: UseQueryResult<User[]>;
  teams: UseQueryResult<any[]>;
  isLoading: boolean;
  error: Error | null;
} => {
  const users = useAllUsers(organizationUUID, headerObj);
  const teams = useTeams(organizationUUID);

  return {
    users,
    teams,
    isLoading: users.isLoading || teams.isLoading,
    error: users.error || teams.error,
  };
};

// Hook for updating user role
export const useUpdateUserRole = (organizationUUID: string | undefined) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async ({ user, newRole }: { user: User; newRole: string }) => {
      return await assignUserRole(user.UserUUID, newRole);
    },
    onMutate: async ({ user, newRole }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['users'] });
      await queryClient.cancelQueries({ queryKey: ['teams'] });

      // Snapshot the previous value
      const previousUsers = queryClient.getQueryData([
        'users',
        'all',
        organizationUUID,
      ]);
      const previousTeams = queryClient.getQueryData([
        'teams',
        'all',
        organizationUUID,
      ]);

      // Optimistically update the user's role
      queryClient.setQueryData(
        ['users', 'all', organizationUUID],
        (old: any) => {
          if (!old) return old;
          return old.map((u: User) =>
            u.UserUUID === user.UserUUID ? { ...u, Role: newRole } : u
          );
        }
      );

      // Return a context object with the snapshotted value
      return { previousUsers, previousTeams };
    },
    onError: (error, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousUsers) {
        queryClient.setQueryData(
          ['users', 'all', organizationUUID],
          context.previousUsers
        );
      }
      if (context?.previousTeams) {
        queryClient.setQueryData(
          ['teams', 'all', organizationUUID],
          context.previousTeams
        );
      }

      toast({
        title: 'Failed to update role',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
    onSuccess: () => {
      // Invalidate seat availability cache when role changes
      queryClient.invalidateQueries({
        queryKey: ['seatAvailability', organizationUUID],
      });

      // Invalidate all user and team queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['users', 'all'] });
      queryClient.invalidateQueries({ queryKey: ['teams', 'all'] });
      // The Settings > Team table is served by the paginated members
      // endpoint, which keys on ['orgMembers'] and would otherwise keep showing
      // pre-mutation rows until its staleTime expired.
      queryClient.invalidateQueries({ queryKey: ['orgMembers'] });

      toast({
        title: 'Role updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
  });
};

// Hook for creating a new team
export const useCreateTeam = (organizationUUID: string | undefined) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (teamData: CreateTeamRequest) => {
      return await createTeam(teamData);
    },
    onSuccess: () => {
      // Invalidate teams query to refetch the updated list
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['teams', 'all'] });
      // The Settings > Team table is served by the paginated members
      // endpoint, which keys on ['orgMembers'] and would otherwise keep showing
      // pre-mutation rows until its staleTime expired.
      queryClient.invalidateQueries({ queryKey: ['orgMembers'] });
    },
    onError: (error) => {
      toast({
        title: 'Failed to create team',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });
};

// Hook for deleting a team
export const useDeleteTeam = (organizationUUID: string | undefined) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (teamId: string) => {
      return await deleteTeam(teamId);
    },
    onSuccess: () => {
      // Invalidate teams query to refetch the updated list
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['teams', 'all'] });
      // The Settings > Team table is served by the paginated members
      // endpoint, which keys on ['orgMembers'] and would otherwise keep showing
      // pre-mutation rows until its staleTime expired.
      queryClient.invalidateQueries({ queryKey: ['orgMembers'] });

      toast({
        title: 'Team deleted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to delete team',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });
};

// Hook for adding user to team
export const useAddUserToTeam = (organizationUUID: string | undefined) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async ({
      teamId,
      userId,
    }: {
      teamId: string;
      userId: string;
    }) => {
      return await addUserToTeam(teamId, userId);
    },
    onSuccess: () => {
      // Invalidate seat availability cache when user is added to team
      queryClient.invalidateQueries({
        queryKey: ['seatAvailability', organizationUUID],
      });

      // Invalidate both users and teams queries
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['users', 'all'] });
      queryClient.invalidateQueries({ queryKey: ['teams', 'all'] });
      // The Settings > Team table is served by the paginated members
      // endpoint, which keys on ['orgMembers'] and would otherwise keep showing
      // pre-mutation rows until its staleTime expired.
      queryClient.invalidateQueries({ queryKey: ['orgMembers'] });

      toast({
        title: 'User added to team successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to add user to team',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });
};

// Hook for removing user from team
export const useRemoveUserFromTeam = (organizationUUID: string | undefined) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async ({
      teamId,
      userId,
    }: {
      teamId: string;
      userId: string;
    }) => {
      return await removeUserFromTeam(teamId, userId);
    },
    onSuccess: () => {
      // Invalidate seat availability cache when user is removed from team
      queryClient.invalidateQueries({
        queryKey: ['seatAvailability', organizationUUID],
      });

      // Invalidate both users and teams queries
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['users', 'all'] });
      queryClient.invalidateQueries({ queryKey: ['teams', 'all'] });
      // The Settings > Team table is served by the paginated members
      // endpoint, which keys on ['orgMembers'] and would otherwise keep showing
      // pre-mutation rows until its staleTime expired.
      queryClient.invalidateQueries({ queryKey: ['orgMembers'] });

      toast({
        title: 'User removed from team successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to remove user from team',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });
};

// Hook for creating a new user
export const useCreateUser = (
  organizationUUID: string | undefined,
  headerObj: headerObject
) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (userData: {
      FirstName?: string;
      LastName?: string;
      Email?: string;
      Image?: string;
      Role?: string;
      TeamUUID?: string;
      username?: string;
      provider?: string;
      companyEmail?: string;
      designation?: string;
    }) => {
      return await createUser(
        userData.FirstName,
        userData.LastName,
        userData.Email,
        userData.Image,
        userData.Role,
        headerObj,
        userData.TeamUUID,
        organizationUUID,
        userData.username,
        userData.provider,
        userData.companyEmail,
        userData.designation
      );
    },
    onSuccess: () => {
      // Invalidate users query to refetch the updated list
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['users', 'all'] });

      toast({
        title: 'User created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to create user',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });
};

// Hook for deleting a user
export const useDeleteUser = (
  organizationUUID: string | undefined,
  accessToken: string | undefined
) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    /**
     * Accepts the member's own organization alongside their UUID.
     *
     * The Settings > Team roster spans every org the viewer has combined into
     * their view, so a listed member does not necessarily belong to the org
     * the viewer is managing. Sending the viewer's org there would aim the
     * removal at an org the member is not in: it fails, or worse, removes a
     * different membership. Callers holding a row should pass its
     * `OrganizationUUID`; a bare UUID still falls back to the viewer's org for
     * callers that only ever list one.
     */
    mutationFn: async (
      target: string | { userUUID: string; organizationUUID?: string }
    ) => {
      const userUUID = typeof target === 'string' ? target : target.userUUID;
      const targetOrgUUID =
        (typeof target === 'string' ? undefined : target.organizationUUID) ||
        organizationUUID ||
        '';
      return await removeMemberFromOrganizationRequest(
        { UserUUID: userUUID, OrgUUID: targetOrgUUID },
        { Authorization: `Bearer ${accessToken}` }
      );
    },
    onSuccess: () => {
      // Invalidate seat availability cache when user is deleted
      queryClient.invalidateQueries({
        queryKey: ['seatAvailability', organizationUUID],
      });

      // Invalidate both users and teams queries
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      queryClient.invalidateQueries({ queryKey: ['users', 'all'] });
      queryClient.invalidateQueries({ queryKey: ['teams', 'all'] });
      // The Settings > Team table is served by the paginated members
      // endpoint, which keys on ['orgMembers'] and would otherwise keep showing
      // pre-mutation rows until its staleTime expired.
      queryClient.invalidateQueries({ queryKey: ['orgMembers'] });

      toast({
        title: 'User deleted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to delete user',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });
};
